import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BadgeCheck, ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { pickTranslation } from '../i18n/pickTranslation';
import { SEO } from '@/components/SEO';
import { ShareListingButton } from '@/components/ShareListingButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple scooter card component
const ListingCard = ({ listing }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const localizedTitle =
    pickTranslation(listing, 'title', i18n.language) ||
    listing.title ||
    listing.details?.model ||
    t('scooters.providerPublic.fallbackScooter');

  return (
    <div
      onClick={() => navigate(`/scooters/${listing.service_id}`)}
      className="card-dashboard overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
      data-testid={`listing-${listing.service_id}`}
    >
      <div className="aspect-video bg-background-secondary overflow-hidden">
        <img
          src={listing.images?.[0] || 'https://customer-assets.emergentagent.com/job_journey-link-13/artifacts/j9icv8bt_Image.png'}
          alt={localizedTitle}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <h3 className="font-semibold text-primary line-clamp-1">
              {localizedTitle}
            </h3>
            {listing.details?.brand && (
              <p className="text-sm text-ink-light">{listing.details.brand}</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
          <div>
            <span className="text-lg font-bold text-primary">{listing.price}฿</span>
            <span className="text-sm text-ink-light"> {t('scooters.providerPublic.pricePerDay')}</span>
          </div>
          {listing.location && (
            <span className="text-sm text-ink-light">{listing.location}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export const ProviderListings = () => {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = String(i18n.resolvedLanguage || i18n.language || 'fr').toLowerCase().startsWith('en') ? 'en' : 'fr';
  // Public provider data (services/provider_pages.py): the COMMERCIAL name (a neutral label when there is none),
  // the verified badge and the page's own SEO texts in the current language. 404 for a provider that is not
  // approved or has nothing public: the page then says « Prestataire introuvable ».
  const [provider, setProvider] = useState(null);
  const [listings, setListings] = useState([]);
  const [seoDone, setSeoDone] = useState(false);
  const [listingsDone, setListingsDone] = useState(false);
  // A language change asks for the texts again without hiding the page (the provider is already known).
  const loading = !listingsDone || (!provider && !seoDone);
  const [error, setError] = useState(null);

  // Provider's active scooters: we omit `status` so the backend applies its public default (`approved`) — the
  // frontend never guesses the status value and stays in sync if that default evolves. Both requests start at
  // once (they do not depend on each other); the texts are asked again only when the language changes.
  useEffect(() => {
    if (!providerId) return undefined;
    let cancelled = false;
    setListingsDone(false);
    setError(null);
    axios.get(`${API}/services`, { params: { provider_id: providerId, category: 'scooter' } })
      .then((res) => { if (!cancelled) setListings(res.data || []); })
      .catch((err) => {
        console.error('Error fetching provider listings:', err);
        if (!cancelled) setError(t('scooters.providerPublic.loadError'));
      })
      .finally(() => { if (!cancelled) setListingsDone(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  useEffect(() => {
    if (!providerId) return undefined;
    let cancelled = false;
    setSeoDone(false);
    setProvider((p) => (p && p.provider_id !== providerId ? null : p));
    axios.get(`${API}/seo/provider/${encodeURIComponent(providerId)}`, { params: { lang } })
      .then((res) => { if (!cancelled) setProvider(res.data); })
      .catch((err) => {
        if (cancelled) return;
        setProvider(null);
        if (err?.response?.status !== 404) setError(t('scooters.providerPublic.loadError'));
      })
      .finally(() => { if (!cancelled) setSeoDone(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, lang]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-ink-light">{t('scooters.providerPublic.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-ink-light mb-4">{error || t('scooters.providerPublic.notFound')}</p>
          <button
            onClick={() => navigate('/scooters')}
            className="text-accent hover:text-accent/80 transition-colors"
          >
            {t('scooters.providerPublic.backToListings')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={provider.seo_title || t('seo.provider.title', { name: provider.name })}
        description={provider.meta_description || t('seo.provider.description', { name: provider.name })}
      />
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-ink-light hover:text-primary transition-colors mb-6"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
            {t('scooters.detail.back')}
          </button>

          {/* Provider Info */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center text-white font-bold text-2xl">
              {String(provider.name || '?')[0]}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold text-primary">{provider.name}</h1>
                {provider.verified && <BadgeCheck className="w-6 h-6 text-accent" />}
              </div>
              <p className="text-ink-light" data-testid="provider-listings-count">
                {t('scooters.providerPublic.listingsCount', { count: listings.length })}
              </p>
              <ShareListingButton category="provider" listingId={provider.provider_id}
                                  title={provider.has_business_name ? provider.name : ''} className="mt-3" />
            </div>
          </div>
        </div>
      </div>

      {/* Listings Grid */}
      <div className="container mx-auto px-4 py-8">
        {listings.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-ink-light">{t('scooters.providerPublic.emptyState')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((listing) => (
              <ListingCard
                key={listing.service_id}
                listing={listing}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
